import { jflApi } from "../common/authApi";
import { regionName, sortByName } from "./functions";

const errorMessage = (error, fallback) =>
  error.response?.data?.error?.errorMessage ||
  error.response?.data?.message ||
  error.message ||
  fallback;

// GET /v1/regions. Each entry is read through regionName, so the list comes
// back as plain names whether the service sends strings or objects.
export const fetchRegions = async () => {
  try {
    const response = await jflApi.get("/v1/regions");
    const payload = response.data?.data;
    const regions = Array.isArray(payload) ? payload : payload?.regions ?? [];

    return {
      success: true,
      data: regions.map(regionName).filter(Boolean),
    };
  } catch (error) {
    return { success: false, message: errorMessage(error, "Could not load regions") };
  }
};

// POST /v1/stores/region. One region for every store code in the batch.
// Goes through jflApi (the signed-in session's cookies) rather than the
// Basic keyId:rawKey header the service also accepts - that key is a server
// credential and must not ship in the browser bundle.
export const assignStoreRegion = async (storeCodes, region) => {
  try {
    const response = await jflApi.post("/v1/stores/region", { storeCodes, region });

    return { success: true, data: response.data?.data ?? null };
  } catch (error) {
    return { success: false, message: errorMessage(error, "Could not update region") };
  }
};

// GET /v1/location/states -> [{ _id, name }], sorted by name.
export const fetchStates = async () => {
  try {
    const response = await jflApi.get("/v1/location/states");
    const states = response.data?.data?.states ?? [];
    return { success: true, data: sortByName(states) };
  } catch (error) {
    return { success: false, message: errorMessage(error, "Could not load states") };
  }
};

// GET /v1/location/cities?stateId= -> [{ _id, name, stateId }], sorted by name.
// Each state's list is read once per visit; picking it again is answered from
// here.
const citiesByState = new Map();

export const fetchCities = async (stateId) => {
  if (citiesByState.has(stateId)) return { success: true, data: citiesByState.get(stateId) };
  try {
    const response = await jflApi.get("/v1/location/cities", { params: { stateId } });
    const cities = sortByName(response.data?.data?.cities ?? []);
    citiesByState.set(stateId, cities);
    return { success: true, data: cities };
  } catch (error) {
    return { success: false, message: errorMessage(error, "Could not load cities") };
  }
};

// POST /v1/stores/location. One city for every store code in the batch; the
// service sets the state from the city.
export const assignStoreLocation = async (storeCodes, cityId) => {
  try {
    const response = await jflApi.post("/v1/stores/location", { storeCodes, cityId });
    return { success: true, data: response.data?.data ?? null };
  } catch (error) {
    return { success: false, message: errorMessage(error, "Could not update location") };
  }
};
